import * as yup from "yup"
import { toast } from "react-toastify"
import { useAuthControllerLogoutMutation } from "~/ui-rtk/api/authApi"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"
import { isUnauthorizedError } from "~/ui-rtk/utils/http-utils"
import { useCallback, useMemo } from "react"
import { CompanySetupStatus } from "~/ui-rtk/api/types"
import useCompanyUpdater from "~/ui-rtk/hooks/company-updater"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useAccess from "~/ui-rtk/hooks/access"
import {
  buildRegionalMetadata,
  regionalFormValues,
  type RegionalFormValues,
} from "./regional-metadata"

export const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD - US Dollar" },
  { value: "CAD", label: "CAD - Canadian Dollar" },
]

/** A bare host like example.com — no scheme, no path. Empty is allowed (never set). */
export const BARE_HOST_PATTERN =
  /^$|^(?=.{1,253}$)(?!-)(?:[a-z0-9-]+\.)+[a-z]{2,}$/i

export const validationSchema = yup.object().shape({
  isPipelineEnabled: yup.boolean().default(false),
  freeAccessEndsAt: yup.date().nullable().optional(),
  isBillingEnabled: yup.boolean().optional().default(false),
  industry: yup.string(),
  domain: yup
    .string()
    .matches(BARE_HOST_PATTERN, "Enter a bare host like example.com")
    .optional(),
  gaMultiProperty: yup.boolean().default(false),
  isAgencyClient: yup.boolean().default(false),
  timezone: yup.string().optional(),
  currency: yup.string().optional(),
  countryBreakoutEnabled: yup.boolean().default(false),
  usaCampaignSlug: yup.string().optional(),
  canadaCampaignSlug: yup.string().optional(),
})

export type TCompanySettingsFormValues = yup.InferType<
  typeof validationSchema
> &
  // The GA4 property → country map is a plain record owned by
  // CountryBreakoutGaMap, not a yup field.
  Required<Pick<RegionalFormValues, "gaPropertyCountries">>

export function useConnect() {
  const currentCompany = useCurrentCompany()
  const { updateCompany } = useCompanyUpdater()
  const { isSuperAdmin } = useAccess()

  const [logout] = useAuthControllerLogoutMutation()
  const navigate = useStableNavigate()

  const initialValues: TCompanySettingsFormValues = useMemo(() => {
    const metadata = currentCompany?.metadata as
      | Record<string, unknown>
      | undefined
    return {
      industry: (metadata?.industry as string) || "",
      domain: currentCompany?.domain || "",
      isPipelineEnabled: Boolean(metadata?.isPipelineEnabled),
      freeAccessEndsAt: metadata?.freeAccessEndsAt
        ? new Date(metadata?.freeAccessEndsAt as string)
        : null,
      isBillingEnabled: Boolean(metadata?.isBillingEnabled),
      gaMultiProperty: Boolean(
        (
          (metadata?.settings as Record<string, unknown>)?.flags as Record<
            string,
            unknown
          >
        )?.ga_multi_property,
      ),
      timezone: (metadata?.timezone as string) || "",
      currency: (metadata?.currency as string) || "USD",
      isAgencyClient: Boolean(currentCompany?.isAgencyClient),
      ...regionalFormValues(currentCompany?.metadata?.regional),
    }
  }, [currentCompany])

  const updateSettings = useCallback(
    async (values: TCompanySettingsFormValues) => {
      if (!currentCompany?.id) {
        return
      }

      try {
        const domain = values.domain?.trim().toLowerCase()
        const updated = await updateCompany({
          setupStatus: values.isBillingEnabled
            ? CompanySetupStatus.PENDING_PAYMENT
            : CompanySetupStatus.INITIAL_SETUP,
          // The brand domain the destination audit judges against; only sent
          // when set, so clearing the field never wipes the record.
          ...(domain ? { domain } : {}),
          // The API refuses this field from anyone but a super admin.
          ...(isSuperAdmin ? { isAgencyClient: values.isAgencyClient } : {}),
          metadata: {
            isPipelineEnabled: values.isPipelineEnabled,
            freeAccessEndsAt: values.freeAccessEndsAt?.toISOString(),
            isBillingEnabled: values.isBillingEnabled,
            industry: values.industry,
            timezone: values.timezone,
            currency: values.currency,
            settings: {
              ...(currentCompany?.metadata?.settings ?? {}),
              flags: {
                ...(currentCompany?.metadata?.settings?.flags ?? {}),
                ga_multi_property: values.gaMultiProperty ?? false,
              },
            },
            // Rebuilt from the form as a whole: the API merges metadata one
            // level deep, so this replaces any previous `regional` object.
            regional: buildRegionalMetadata(values),
          },
        })
        if (updated) {
          toast.success("Settings were updated")
        } else {
          toast.error("Could not update company settings")
        }
      } catch (error) {
        if (isUnauthorizedError(error)) {
          await logout()
          navigate("/")
          return
        }
        toast.error("Could not update company settings")
      }
    },
    [currentCompany, isSuperAdmin],
  )

  return {
    currentCompany,
    initialValues,
    validationSchema,
    updateSettings,
  }
}
